import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { loadCustomerGroups } from '../../../../crud/integration.crud'
import { fetchCustomerEngageEmployees } from '../../../../util/employees.util'
import { GWS } from '../../engage/utils'

const EMPTY_EMPLOYEES = []
const EMPTY_GROUPS = []

export const adaptGwsTenantGroups = (groups = []) =>
  groups
    .map((group) => ({
      id: group?.id ?? group?.groupId,
      name: group?.name ?? group?.displayName ?? '',
      groupType: group?.groupType ?? group?.type ?? GWS,
    }))
    .filter((group) => group.id != null)

export const mergeCurrentEmployee = (catalogEmployees = [], currentEmployee) => {
  if (!currentEmployee) {
    return catalogEmployees
  }

  const currentEmployeeId = currentEmployee.employeeId ?? currentEmployee.id
  const withoutCurrent = catalogEmployees.filter(
    (employee) => (employee?.employeeId ?? employee?.id) !== currentEmployeeId,
  )

  return [currentEmployee, ...withoutCurrent]
}

const getGroupsPayload = (response) => {
  const payload = response?.data
  if (Array.isArray(payload)) {
    return payload
  }
  if (Array.isArray(payload?.data)) {
    return payload.data
  }
  return []
}

const useGwsEngageActionCatalog = ({
  enabled = false,
  selectedCustomer,
  selectedIntegration,
  currentEmployee,
} = {}) => {
  const [catalogEmployees, setCatalogEmployees] = useState(EMPTY_EMPLOYEES)
  const [tenantGroups, setTenantGroups] = useState(EMPTY_GROUPS)
  const organizationIdFromUser = useSelector(({ user }) => user?.data?.Customer?.organization)
  const organizationId = selectedCustomer?.organization ?? organizationIdFromUser
  const customerId = selectedCustomer?.id
  const integrationApplicationId = selectedIntegration?.IntegrationApplication?.id

  useEffect(() => {
    if (!enabled || !customerId || organizationId == null) {
      setCatalogEmployees(EMPTY_EMPLOYEES)
      setTenantGroups(EMPTY_GROUPS)
      return undefined
    }

    let cancelled = false

    const loadEmployees = async () => {
      try {
        const employees = await fetchCustomerEngageEmployees({
          customerId,
          integrationName: GWS,
          organizationId,
          license: false,
        })

        if (!cancelled) {
          setCatalogEmployees(employees || EMPTY_EMPLOYEES)
        }
      } catch (error) {
        console.error('Error fetching Google Workspace employee catalog:', error)
        if (!cancelled) {
          setCatalogEmployees(EMPTY_EMPLOYEES)
        }
      }
    }

    const loadGroups = async () => {
      if (!integrationApplicationId) {
        if (!cancelled) {
          setTenantGroups(EMPTY_GROUPS)
        }
        return
      }

      try {
        const groupsResponse = await loadCustomerGroups(customerId, integrationApplicationId)
        if (!cancelled) {
          setTenantGroups(adaptGwsTenantGroups(getGroupsPayload(groupsResponse)))
        }
      } catch (error) {
        console.error('Error fetching Google Workspace group catalog:', error)
        if (!cancelled) {
          setTenantGroups(EMPTY_GROUPS)
        }
      }
    }

    loadEmployees()
    loadGroups()

    return () => {
      cancelled = true
    }
  }, [enabled, customerId, organizationId, integrationApplicationId])

  const employees = useMemo(
    () => mergeCurrentEmployee(catalogEmployees, currentEmployee),
    [catalogEmployees, currentEmployee],
  )

  const groups = useMemo(() => {
    if (tenantGroups.length > 0) {
      return tenantGroups
    }

    const fromEmployees = adaptGwsTenantGroups(employees.flatMap((employee) => employee?.groups || []))
    return fromEmployees
  }, [tenantGroups, employees])

  return { employees, groups }
}

export default useGwsEngageActionCatalog
